/**
 * Componente para crear una nueva empresa
 * 
 * Formulario completo para registrar una nueva empresa en el sistema con todos
 * los campos necesarios para la gestión empresarial.
 * 
 * @component
 * @example
 * // Usar en una ruta protegida
 * <Route path="/gestion-usuarios/nueva-empresa" element={<NuevaEmpresa />} />
 * 
 * @author Ana Lizcano y Alejandro
 * @version 1.0.1
 * @since 2025-07-10
 */

import { useState } from "react";
import { useNavigate } from "react-router-dom";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDSnackbar from "components/MDSnackbar";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

// Componente personalizado para selección de países
import CountrySelector from "components/Api/CountrySelector";

/**
 * Componente principal para la creación de nuevas empresas
 * 
 * Maneja el formulario completo con validaciones, notificaciones y navegación.
 * Incluye campos para información básica, contacto, clasificación y ubicación.
 * 
 * @returns {JSX.Element} Componente de formulario para nueva empresa
 */
function NuevaEmpresa() {
  // Hook para navegación programática
  const navigate = useNavigate();

  /**
   * Estado del formulario con todos los campos de la empresa
   * @type {Object}
   */
  const [formData, setFormData] = useState({
    nombre: "",
    nit: "",
    email: "",
    telefono: "",
    descripcion: "",
    sector_empresa: "",
    tamaño_empresa: "",
    zona_estrategicas: "",
    area_operacion: "",
    pais_operacion: "", // Manejado por CountrySelector
    ciudad_operacion: "",
    estado_empresa: "Activa",
  });

  // Estados para manejo de notificaciones
  const [successSB, setSuccessSB] = useState(false);
  const [errorSB, setErrorSB] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  // Opciones predefinidas para campos select
  const sectores = ["Tecnología", "Energía", "Alimentos", "Salud", "Educación", "Servicios", "Manufactura"];
  const tamaños = ["Pequeña", "Mediana", "Grande"];
  const zonas = ["Zona Norte", "Zona Centro", "Zona Sur", "Zona Este", "Zona Oeste"];
  const areas = ["Local", "Regional", "Nacional", "Internacional"];
  const estados = ["Activa", "Pendiente", "Inactiva"];

  // Funciones para manejo de notificaciones
  const openSuccessSB = () => setSuccessSB(true);
  const closeSuccessSB = () => setSuccessSB(false);
  const openErrorSB = () => setErrorSB(true);
  const closeErrorSB = () => setErrorSB(false);

  /**
   * Maneja los cambios en los campos del formulario
   * @param {string} field - Nombre del campo a actualizar
   * @returns {Function} Función para manejar el evento de cambio
   */
  const handleChange = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  /**
   * Maneja la selección de país desde el CountrySelector
   * @param {string} selectedCountry - País seleccionado
   */
  const handleCountrySelect = (selectedCountry) => {
    setFormData(prev => ({
      ...prev,
      pais_operacion: selectedCountry
    }));
  };

  /**
   * Maneja el envío del formulario
   * Valida campos obligatorios, simula creación y redirige
   * @param {Event} event - Evento de envío del formulario
   */
  const handleSubmit = (event) => {
    event.preventDefault();

    // Validaciones de campos obligatorios
    if (!formData.nombre || !formData.nit || !formData.email || !formData.pais_operacion) { 
      setSnackbarMessage("Por favor complete los campos obligatorios: Nombre, NIT, Email y País de Operación");
      openErrorSB();
      return;
    }

    // TODO: Implementar llamada a la API
    console.log("Creando empresa:", formData);

    // Simular éxito en la creación
    setSnackbarMessage(`Empresa "${formData.nombre}" creada exitosamente`);
    openSuccessSB();

    // Redirigir después de mostrar notificación
    setTimeout(() => {
      navigate("/gestion-usuarios/empresas");
    }, 2000);
  };

  /**
   * Maneja la cancelación del formulario
   * Redirige a la lista de empresas sin guardar
   */
  const handleCancel = () => {
    navigate("/gestion-usuarios/empresas");
  };

  /**
   * Reinicia todos los campos del formulario a sus valores iniciales
   */
  const resetForm = () => {
    setFormData({
      nombre: "",
      nit: "",
      email: "",
      telefono: "",
      descripcion: "",
      sector_empresa: "",
      tamaño_empresa: "",
      zona_estrategicas: "",
      area_operacion: "",
      pais_operacion: "",
      ciudad_operacion: "",
      estado_empresa: "Activa",
    });
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="primary" // Cambiado de "success" a "primary"
                borderRadius="lg"
                coloredShadow="primary" // Cambiado de "success" a "primary"
              >
                <MDTypography variant="h6" color="white">
                  Nueva Empresa
                </MDTypography>
              </MDBox>

              <MDBox pt={3} pb={3} px={3}>
                <form onSubmit={handleSubmit}>
                  <Grid container spacing={3}>
                    {/* Fila 1 - Información básica */}
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Nombre de la Empresa *"
                        value={formData.nombre}
                        onChange={handleChange("nombre")}
                        variant="outlined"
                        required
                        InputLabelProps={{ shrink: true }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            height: '56px',
                            '& input': {
                              padding: '16.5px 14px'
                            }
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="NIT *"
                        value={formData.nit}
                        onChange={handleChange("nit")}
                        variant="outlined"
                        required
                        InputLabelProps={{ shrink: true }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            height: '56px',
                            '& input': {
                              padding: '16.5px 14px'
                            }
                          }
                        }}
                      />
                    </Grid>

                    {/* Fila 2 - Contacto */}
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Email *"
                        type="email"
                        value={formData.email}
                        onChange={handleChange("email")}
                        variant="outlined"
                        required
                        InputLabelProps={{ shrink: true }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            height: '56px',
                            '& input': {
                              padding: '16.5px 14px'
                            }
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Teléfono"
                        value={formData.telefono}
                        onChange={handleChange("telefono")}
                        variant="outlined"
                        InputLabelProps={{ shrink: true }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            height: '56px',
                            '& input': {
                              padding: '16.5px 14px'
                            }
                          }
                        }}
                      />
                    </Grid>

                    {/* Fila 3 - Descripción */}
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Descripción"
                        multiline
                        rows={3}
                        value={formData.descripcion}
                        onChange={handleChange("descripcion")}
                        variant="outlined"
                        placeholder="Describa brevemente la empresa..."
                      />
                    </Grid>

                    {/* Fila 4 - Sector y Tamaño */}
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        select
                        label="Sector / Industria"
                        placeholder="Seleccione un sector"
                        value={formData.sector_empresa}
                        onChange={handleChange("sector_empresa")}
                        variant="outlined"
                        InputLabelProps={{ shrink: true }}
                        SelectProps={{
                          displayEmpty: true,
                          native: false,
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            height: '56px',
                            '& .MuiSelect-select': {
                              padding: '16.5px 14px',
                              display: 'flex',
                              alignItems: 'center'
                            }
                          }
                        }}
                      >
                        <MenuItem value="">
                          <em>Seleccione un sector</em>
                        </MenuItem>
                        {sectores.map((sector) => (
                          <MenuItem key={sector} value={sector}>
                            {sector}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        select
                        label="Tamaño de la Empresa"
                        placeholder="Seleccione el tamaño"
                        value={formData.tamaño_empresa}
                        onChange={handleChange("tamaño_empresa")}
                        variant="outlined"
                        InputLabelProps={{ shrink: true }}
                        SelectProps={{
                          displayEmpty: true,
                          native: false,
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            height: '56px',
                            '& .MuiSelect-select': {
                              padding: '16.5px 14px',
                              display: 'flex',
                              alignItems: 'center'
                            }
                          }
                        }}
                      >
                        <MenuItem value="">
                          <em>Seleccione el tamaño</em>
                        </MenuItem>
                        {tamaños.map((tamaño) => (
                          <MenuItem key={tamaño} value={tamaño}>
                            {tamaño}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>

                    {/* Fila 5 - Zona y Área */}
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        select
                        label="Zona Estratégica"
                        placeholder="Seleccione una zona"
                        value={formData.zona_estrategicas}
                        onChange={handleChange("zona_estrategicas")}
                        variant="outlined"
                        InputLabelProps={{ shrink: true }}
                        SelectProps={{
                          displayEmpty: true,
                          native: false,
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            height: '56px',
                            '& .MuiSelect-select': {
                              padding: '16.5px 14px',
                              display: 'flex',
                              alignItems: 'center'
                            }
                          }
                        }}
                      >
                        <MenuItem value="">
                          <em>Seleccione una zona</em>
                        </MenuItem>
                        {zonas.map((zona) => (
                          <MenuItem key={zona} value={zona}>
                            {zona}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        select
                        label="Área de Operación"
                        placeholder="Seleccione el área"
                        value={formData.area_operacion}
                        onChange={handleChange("area_operacion")}
                        variant="outlined"
                        InputLabelProps={{ shrink: true }}
                        SelectProps={{
                          displayEmpty: true,
                          native: false,
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            height: '56px',
                            '& .MuiSelect-select': {
                              padding: '16.5px 14px',
                              display: 'flex',
                              alignItems: 'center'
                            }
                          }
                        }}
                      >
                        <MenuItem value="">
                          <em>Seleccione el área</em>
                        </MenuItem>
                        {areas.map((area) => (
                          <MenuItem key={area} value={area}>
                            {area}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>

                    {/* Fila 6 - Ubicación */}
                    <Grid item xs={12} md={6}>
                      {/* Aquí es donde colocas el CountrySelector */}
                      <CountrySelector
                        onSelect={handleCountrySelect} // Pasar la función para manejar el cambio
                        selectedCountryValue={formData.pais_operacion} // Pasar el valor actual del formulario
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Ciudad de Operación"
                        value={formData.ciudad_operacion}
                        onChange={handleChange("ciudad_operacion")}
                        variant="outlined"
                        placeholder="Ej: Bogotá"
                        InputLabelProps={{ shrink: true }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            height: '56px',
                            '& input': {
                              padding: '16.5px 14px'
                            }
                          }
                        }}
                      />
                    </Grid>

                    {/* Fila 7 - Estado */}
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        select
                        label="Estado de la Empresa"
                        placeholder="Seleccione el estado"
                        value={formData.estado_empresa}
                        onChange={handleChange("estado_empresa")}
                        variant="outlined"
                        InputLabelProps={{ shrink: true }}
                        SelectProps={{
                          displayEmpty: true,
                          native: false,
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            height: '56px',
                            '& .MuiSelect-select': {
                              padding: '16.5px 14px',
                              display: 'flex',
                              alignItems: 'center'
                            }
                          }
                        }}
                      >
                        {estados.map((estado) => (
                          <MenuItem key={estado} value={estado}>
                            {estado}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                  </Grid>

                  {/* Botones de acción */}
                  <MDBox mt={4} display="flex" justifyContent="space-between" alignItems="center">
                    <MDBox display="flex" gap={2}>
                      <MDButton
                        variant="outlined"
                        color="secondary"
                        onClick={handleCancel}
                      >
                        Cancelar
                      </MDButton>
                      <MDButton
                        variant="outlined"
                        color="warning"
                        onClick={resetForm}
                      >
                        Limpiar Formulario
                      </MDButton>
                    </MDBox>

                    <MDButton
                      type="submit"
                      variant="gradient"
                      color="success"
                    >
                      Crear Empresa
                    </MDButton>
                  </MDBox>
                </form>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <Footer />

      {/* Snackbar para notificaciones */}
      <MDSnackbar
        color="success"
        icon="check"
        title="Éxito"
        content={snackbarMessage}
        dateTime=""
        open={successSB}
        onClose={closeSuccessSB}
        close={closeSuccessSB}
        bgWhite
      />

      <MDSnackbar
        color="error"
        icon="warning"
        title="Error"
        content={snackbarMessage}
        dateTime=""
        open={errorSB}
        onClose={closeErrorSB}
        close={closeErrorSB}
        bgWhite
      />
    </DashboardLayout>
  );
}

export default NuevaEmpresa;